import { useParams, Link } from 'react-router-dom';
import { articlesData } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const articleDetailImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Chinese%20gaming%20bonus%20registration%20guide%20concept%20with%20red%20and%20gold%20decorative%20elements%2C%20elegant%20professional%20editorial%20illustration%2C%20warm%20luxurious%20background%2C%20soft%20lighting&width=1200&height=600&seq=article-detail-01&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=Slot%20machine%20gaming%20strategy%20tips%20illustration%20with%20Chinese%20red%20gold%20decorative%20elements%2C%20warm%20professional%20editorial%20style%2C%20luxurious%20background&width=1200&height=600&seq=article-detail-02&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Platform%20comparison%20analysis%20chart%20with%20Chinese%20red%20gold%20decorative%20elements%2C%20professional%20editorial%20illustration%2C%20warm%20clean%20background&width=1200&height=600&seq=article-detail-03&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=Mahjong%20and%20poker%20Chinese%20chess%20card%20games%20illustration%20with%20red%20gold%20decorative%20elements%2C%20warm%20professional%20editorial%20style&width=1200&height=600&seq=article-detail-04&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=Customer%20support%20help%20desk%20FAQ%20illustration%20with%20Chinese%20red%20gold%20decorative%20elements%2C%20warm%20professional%20editorial%20style&width=1200&height=600&seq=article-detail-05&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=Veteran%20gamer%20sharing%20experience%20wisdom%20illustration%20with%20Chinese%20red%20gold%20elements%2C%20warm%20professional%20editorial%20style&width=1200&height=600&seq=article-detail-06&orientation=landscape',
  7: 'https://readdy.ai/api/search-image?query=New%20player%20registration%20journey%20step%20by%20step%20illustration%20with%20Chinese%20red%20gold%20elements%2C%20warm%20welcoming%20editorial%20style&width=1200&height=600&seq=article-detail-07&orientation=landscape',
  8: 'https://readdy.ai/api/search-image?query=Sports%20betting%20stadium%20strategy%20illustration%20with%20Chinese%20red%20gold%20decorative%20elements%2C%20warm%20professional%20editorial%20style&width=1200&height=600&seq=article-detail-08&orientation=landscape',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articlesData.find((a) => a.id === articleId) || articlesData[0];

  const relatedArticles = articlesData
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: articleDetailImages[article.id] || articleDetailImages[1],
    publisher: {
      '@type': 'Organization',
      name: '注册送彩金',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.example.com/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '攻略文章', path: '/article/1' },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-8 md:py-12">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
                  {article.category}
                </span>
                <span className="text-foreground-400 text-xs">
                  <i className="ri-time-line mr-1"></i>
                  {article.readTime}
                </span>
                <span className="text-foreground-400 text-xs">
                  <i className="ri-eye-line mr-1"></i>
                  {article.views.toLocaleString()} 阅读
                </span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-foreground-500 text-sm">
                <span>
                  <i className="ri-user-line mr-1"></i>
                  {article.author}
                </span>
                <span>
                  <i className="ri-calendar-line mr-1"></i>
                  {article.date}
                </span>
              </div>
            </div>

            <div className="aspect-[2/1] rounded-xl overflow-hidden mb-8">
              <img
                src={articleDetailImages[article.id] || articleDetailImages[1]}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 mt-8 pt-6 border-t border-background-200/70 flex-wrap">
              <span className="text-foreground-500 text-sm">标签：</span>
              {article.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关文章推荐
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={articleDetailImages[related.id] || articleDetailImages[1]}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-heading text-sm font-bold text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {related.title}
                      </h4>
                      <span className="text-foreground-400 text-xs">{related.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}